import getFundType, { FUND_TYPES } from '../../stores/profile/get-fund-type';
import getAssetClass, { ASSET_CLASSES } from '../../stores/profile/get-asset-class';
import { FIELDS, PRODUCT_TYPE } from '../../shared/screener-constants';
import format from '../format';

export const getDate = () => {
  return new Date();
};

export const findClosestChartDatum = ({ dataSorted = [], xCoordinate }) => {
  if (typeof xCoordinate !== 'number') {
    return undefined;
  }

  const MAX_ITERATIONS = 25;
  let indexStart = 0;
  let indexEnd = dataSorted.length - 1;
  let indexCurrent;

  let count = 0;
  while (indexStart <= indexEnd && count < MAX_ITERATIONS) {
    count++;
    if (indexStart === indexEnd) {
      return dataSorted[indexStart];
    }

    indexCurrent = Math.floor((indexStart + indexEnd) / 2);
    const indexNext = indexCurrent + 1;
    const current = dataSorted[indexCurrent];
    const next = dataSorted[indexNext];

    try {
      if (current.X > xCoordinate) {
        indexEnd = indexCurrent;
      } else if (next.X < xCoordinate) {
        indexStart = indexNext;
      } else {
        return Math.abs(current.X - xCoordinate) -
          Math.abs(next.X - xCoordinate) >
          0
          ? next
          : current;
      }
    } catch (e) {
      console.warn('Error searching hover data'); // eslint-disable-line no-console
      return dataSorted[dataSorted.length - 1];
    }
  }

  return count >= MAX_ITERATIONS
    ? dataSorted[dataSorted.length - 1]
    : dataSorted[indexStart];
};

export const getTextValueColorClass = val => {
  if (val > 0) {
    return 'text-success';
  }
  if (val === -32768 || val === '-32768' || val === '+0.00') {
    return 'text-neutral';
  }
  if (val < 0) {
    return 'text-danger-secondary';
  }
  return 'text-neutral';
};

export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
};

export const isTiaaFixedAnnuity = fundType => {
  const sanitizedFundType = getFundType(fundType);
  if (
    sanitizedFundType === FUND_TYPES.fixedAnnuityTiaa ||
    sanitizedFundType === FUND_TYPES.fixedAnnuityTiaaStableValue ||
    sanitizedFundType === FUND_TYPES.fixedAnnuityTiaaQdia
  ) {
    return true;
  }
  return false;
};

export const isVariableAnnuity = fundType => {
  const sanitizedFundType = getFundType(fundType);
  if (
    sanitizedFundType === FUND_TYPES.variableAnnuity ||
    sanitizedFundType === FUND_TYPES.variableAnnuityCref ||
    sanitizedFundType === FUND_TYPES.variableAnnuityTiaa ||
    sanitizedFundType === FUND_TYPES.variableAnnuityTiaaAccess
  ) {
    return true;
  }
  return false;
};

export const isSignatureResult = fundType => {
  return getFundType(fundType) === FUND_TYPES.fixedAnnuityTiaa;
};

export const isStableValue = fundType => {
  return getFundType(fundType) === FUND_TYPES.fixedAnnuityTiaaStableValue;
};

export const isRealEstate = assetClass => {
  return getAssetClass(assetClass) === ASSET_CLASSES.realEstate;
};

export const shouldShowTicker = (fundType, shareClass) => {
  const sanitizedFundType = getFundType(fundType);
  return !(
    sanitizedFundType === FUND_TYPES.fixedAnnuityTiaaStableValue ||
    sanitizedFundType === FUND_TYPES.otherInvestment ||
    sanitizedFundType === FUND_TYPES.retirePlus ||
    sanitizedFundType === FUND_TYPES.retirePlusPro ||
    sanitizedFundType === FUND_TYPES.fdicInsuredBankProduct ||
    sanitizedFundType === FUND_TYPES.variableAnnuityTiaaAccess ||
    sanitizedFundType === FUND_TYPES.customPortfolioModel ||
    sanitizedFundType === FUND_TYPES.otherInvestment ||
    shareClass === 'TIAA Traditional Annuity'
  );
};

export const shouldShowHypoChart = (fundType, isCIT) => {
  const sanitizedFundType = getFundType(fundType);

  return (
    sanitizedFundType === FUND_TYPES.mutualFund || isVariableAnnuity(fundType) || isCIT
  );
};

export const getScreenerAsOfDate = (rows, field) => {
  const dates = rows.filter(row => row.hasOwnProperty(field));
  const result = dates.sort((a, b) => b[field] - a[field]);
  if (result.length > 0) {
    return result[0][field];
  }
  return undefined;
};

export const mapProductName = productType => {
  if (productType === PRODUCT_TYPE.RETIREPROPLUSMODELS) {
    return 'RetirePlus Pro Models';
  }
  if (productType === PRODUCT_TYPE.RETIREPLUSMODELS) {
    return 'RetirePlus Select';
  }
  return productType;
};

export const tryParseJson = (json, errorMessage) => {
  try {
    return json ? JSON.parse(json) : null;
  } catch {
    if (errorMessage) {
      console.warn(errorMessage); // eslint-disable-line no-console
    }
    return 0;
  }
};

export const isModel = fundType => {
  const sanitizedFundType = getFundType(fundType);
  return sanitizedFundType === FUND_TYPES.retirePlus ||
    sanitizedFundType === FUND_TYPES.retirePlusPro ||
    sanitizedFundType === FUND_TYPES.customPortfolioModel;
};

export const alignSortingKey = (header, performanceQuarterly) => {
  const headerKey = {
    investmentName: FIELDS.TCINVESTMENTNAME,
    productType: FIELDS.PRODUCTTYPE,
    assetClass: FIELDS.ASSETCLASS,
    yearToDate: FIELDS.TCFUNDNAVYTDPCTCHANGE,
    oneYear: performanceQuarterly ? FIELDS.TCAVGANNRETURNQTR1YR : FIELDS.TCAVGANNRETURN1YR,
    threeYear: performanceQuarterly ? FIELDS.TCAVGANNRETURNQTR3YR : FIELDS.TCAVGANNRETURN3YR,
    fiveYear: performanceQuarterly ? FIELDS.TCAVGANNRETURNQTR5YR : FIELDS.TCAVGANNRETURN5YR,
    tenYear: performanceQuarterly ? FIELDS.TCAVGANNRETURNQTR10YR : FIELDS.TCAVGANNRETURN10YR,
    sinceIncep: performanceQuarterly ? FIELDS.TCAVGANNRETURNQTRSI : FIELDS.TCAVGANNRETURNSI,
    netExp: FIELDS.TCNETEXPENSES,
    rating: FIELDS.MSTARRATINGOVERALL
  };
  switch (performanceQuarterly) {
    case 'proj': //current projected
      headerKey.productType = FIELDS.PRODUCTTYPE;
      headerKey.recordDate = FIELDS.TCPROJDISTRECORDDATE;// record date
      headerKey.reinvestDate = FIELDS.TCPROJDISTEXPAYREINVESTDATE;// reinvest date
      headerKey.short = FIELDS.TCPROJDISTSHORTTERMCAPGAIN; //shortTermKey
      headerKey.long = FIELDS.TCPROJDISTLONGTERMCAPGAIN; //longTermKey
      headerKey.total = FIELDS.TCPROJDISTTOTALGAIN; // Total
      headerKey.distibnav = FIELDS.TCPROJDISTTOTALGAINNAVPCT; // %distibnav
      headerKey.nav = FIELDS.TCPROJDISTNAV; // nav
      break;
    case 'fye': //Year end(Default)
      headerKey.productType = FIELDS.PRODUCTTYPE;
      headerKey.recordDate = FIELDS.TCDISTRECORDDATEFYE; //recordDateKey
      headerKey.reinvestDate = FIELDS.TCDISTREINVESTPAYABLEDATEFYE;// exPayableKey reinvest date
      headerKey.income = FIELDS.TCDISTORDINCOMEFYE; //incomeKey
      headerKey.short = FIELDS.TCSHORTTERMCAPGAINSFYE; //shortTermKey
      headerKey.long = FIELDS.TCLONGTERMCAPGAINSFYE; //longTermKey
      headerKey.price = FIELDS.TCDISTNAVANNUAL; //reinvestKey
      headerKey.total = FIELDS.TCTOTADISTRIBUTIONFYE; //totalKey

      break;
    case 'mon': // Month end
      headerKey.productType = FIELDS.PRODUCTTYPE;
      headerKey.recordDate = FIELDS.TCDISTRECORDDATEMONTHLY; //recordDateKey
      headerKey.reinvestDate = FIELDS.TCDISTREINVESTPAYABLEDATEMONTHLY; //exPayableKey reinvest date
      headerKey.income = FIELDS.TCDISTAMOUNTMONTHLY; //incomeKey
      headerKey.price = FIELDS.TCDISTNAVMONTHLY; //reinvestKey
      headerKey.total = FIELDS.TCDISTAMOUNTMONTHLY; //totalKey

      break;
    case 'qtr': // Quarter end
      headerKey.productType = FIELDS.PRODUCTTYPE;
      headerKey.recordDate = FIELDS.TCDISTRECORDDATEQRTLY; //recordDateKey
      headerKey.reinvestDate = FIELDS.TCPROJDISTEXPAYREINVESTDATE;// reinvest date
      headerKey.reinvestDate = FIELDS.TCDISTREINVESTPAYABLEDATEQRTLY; //exPayableKey  reinvest date
      headerKey.income = FIELDS.TCDISTAMOUNTQRTLY; //incomeKey
      headerKey.price = FIELDS.TCDISTNAVQTRLY;//reinvestKey
      headerKey.total = FIELDS.TCDISTAMOUNTQRTLY; //totalKey
      break;


    default:
      break;
  }
  return headerKey[header];
};

export const alignTiaaFixedAnnuityKey = (header, performanceQuarterly) => {
  const headerKey = {
    investmentName: FIELDS.TCINVESTMENTNAME,
    productType: FIELDS.PRODUCTTYPE,
    assetClass: FIELDS.ASSETCLASS,
    oneYear: performanceQuarterly ? FIELDS.TCRATERETURNQTR1YR : FIELDS.TCRATERETURN1YR,
    threeYear: performanceQuarterly ? FIELDS.TCRATERETURNQTR3YR : FIELDS.TCRATERETURN3YR,
    fiveYear: performanceQuarterly ? FIELDS.TCRATERETURNQTR5YR : FIELDS.TCRATERETURN5YR,
    tenYear: performanceQuarterly ? FIELDS.TCRATERETURNQTR10YR : FIELDS.TCRATERETURN10YR,
    sinceIncep: performanceQuarterly ? FIELDS.TCRATERETURNQTRSI : FIELDS.TCRATERETURNSI,
    netExp: FIELDS.TCNETEXPENSES,
    rating: FIELDS.MSTARRATINGOVERALL
  };
  return headerKey[header];
};

export const sortStringOrNumber = (a, b, sortDirection) => {
  if (!a) return 1;
  if (!b) return -1;
  if (typeof a === 'string' || typeof b === 'string') {
    return sortDirection === 'desc' ?
      b.toLowerCase().localeCompare(a.toLowerCase())
      : a.toLowerCase().localeCompare(b.toLowerCase());
  }
  return sortDirection === 'desc' ? b - a : a - b;
};

export const getIsStandardPerformance = (row, performanceQuarterly) => {
  return !(
    row[performanceQuarterly ? FIELDS.TCISLINKEDPERFQTR1YR : FIELDS.TCISLINKEDPERF1YR] > 0 ||
    row[performanceQuarterly ? FIELDS.TCISLINKEDPERFQTR3YR : FIELDS.TCISLINKEDPERF3YR] > 0 ||
    row[performanceQuarterly ? FIELDS.TCISLINKEDPERFQTR5YR : FIELDS.TCISLINKEDPERF5YR] > 0 ||
    row[performanceQuarterly ? FIELDS.TCISLINKEDPERFQTR10YR : FIELDS.TCISLINKEDPERF10YR] > 0);
};

export const sortBy = (header, sortingState) => {
  if (header === sortingState.sortBy) {
    return {
      sortBy: header,
      sortDirection: sortingState.sortDirection === 'asc' ? 'desc' : 'asc'
    };
  }
  return {
    sortBy: header,
    sortDirection: header === 'investmentName' ||
      header === 'productType' ||
      header === 'assetClass' ||
      header === 'recordDate' ||
      header === 'reinvestDate' ||
      header === 'income' ||
      header === 'short' ||
      header === 'long' ||
      header === 'price' ||
      header === 'total' ? 'asc' : 'desc'
  };
};

export const getTextColor = (name, sortingState) => {
  if (sortingState.sortBy === name) {
    return 'text-selected';
  }
  return '';
};

export const checkIfHasClassA = data => {
  return data.some(fund => fund[FIELDS.TCSHARECLASS] === 'Class A');
};

export const sortByFieldToTop = (items, field, value) => {
  return items.sort((a, b) => {
    if (a[field] === value && b[field] !== value) {
      return -1;
    }
    return 1;
  });
};

export const alignData = (item, performanceQuarterly, performanceAsOf) => {
  const yearToDateReturn = { standard: alignYearToDateReturn(item), salesCharge: alignSalesChargeYearToDateReturn(item) };
  const oneYearReturn = { standard: alignOneYearReturn(item, performanceQuarterly), salesCharge: alignSalesChargeOneYearReturn(item, performanceQuarterly) };
  const threeYearReturn = { standard: alignThreeYearReturn(item, performanceQuarterly), salesCharge: alignSalesChargeThreeYearReturn(item, performanceQuarterly) };
  const fiveYearReturn = { standard: alignFiveYearReturn(item, performanceQuarterly), salesCharge: alignSalesChargeFiveYearReturn(item, performanceQuarterly) };
  const tenYearReturn = { standard: alignTenYearReturn(item, performanceQuarterly), salesCharge: alignSalesChargeTenYearReturn(item, performanceQuarterly) };
  const sinceIncep = { standard: alignSinceInception(item, performanceQuarterly), salesCharge: alignSalesChargeSinceInception(item, performanceQuarterly) };
  const inceptionDate = alignInceptionDate(item);
  const asOfDate = alignAsOfData(item, performanceQuarterly, performanceAsOf);

  return {
    yearToDateReturn,
    oneYearReturn,
    threeYearReturn,
    fiveYearReturn,
    tenYearReturn,
    sinceIncep,
    inceptionDate,
    asOfDate
  };
};

export const isShowNetGrossExpenseRatioDisclosure = (NetExpenseValue, GrossExpenseValue) => {
  return (NetExpenseValue && GrossExpenseValue && (NetExpenseValue !== GrossExpenseValue));
};

export const checkIsCrefR4 = xref => {
  if (xref && xref.data && xref.data.className) {
    return xref.data.className === 'Class R4';
  }
  return false;
};

function alignAsOfData(fundData, performanceQuarterly, performanceAsOf) {
  if (performanceQuarterly) {
    const qtrRowAsof = (fundData[FIELDS.TCAVGANNRETURNQTRASOFDATE] || fundData[FIELDS.TCRATERETURNQTRASOFDATE] || null);
    // if (performanceAsOf !== qtrRowAsof) {
    return (qtrRowAsof);
  }

  const AnnRowAsof = (fundData[FIELDS.TCAVGANNRETURNASOFDATE] || fundData[FIELDS.TCRATERETURNASOFDATE] || null);
  return (AnnRowAsof);
}

function alignYearToDateReturn(fundData) {
  return fundData[FIELDS.TCFUNDNAVYTDPCTCHANGE];
}

function alignOneYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCAVGANNRETURNQTR1YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURNQTR1YR] : fundData[FIELDS.TCRATERETURNQTR1YR];
  }
  return fundData[FIELDS.TCAVGANNRETURN1YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURN1YR] : fundData[FIELDS.TCRATERETURN1YR];
}

function alignThreeYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCAVGANNRETURNQTR3YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURNQTR3YR] : fundData[FIELDS.TCRATERETURNQTR3YR];
  }
  return fundData[FIELDS.TCAVGANNRETURN3YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURN3YR] : fundData[FIELDS.TCRATERETURN3YR];
}

function alignFiveYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCAVGANNRETURNQTR5YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURNQTR5YR] : fundData[FIELDS.TCRATERETURNQTR5YR];
  }
  return fundData[FIELDS.TCAVGANNRETURN5YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURN5YR] : fundData[FIELDS.TCRATERETURN5YR];
}

function alignTenYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCAVGANNRETURNQTR10YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURNQTR10YR] : fundData[FIELDS.TCRATERETURNQTR10YR];
  }
  return fundData[FIELDS.TCAVGANNRETURN10YR] !== undefined ? fundData[FIELDS.TCAVGANNRETURN10YR] : fundData[FIELDS.TCRATERETURN10YR];
}

function alignSinceInception(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCAVGANNRETURNQTRSI] !== undefined ? fundData[FIELDS.TCAVGANNRETURNQTRSI] : fundData[FIELDS.TCRATERETURNQTRSI];
  }
  return fundData[FIELDS.TCAVGANNRETURNSI] !== undefined ? fundData[FIELDS.TCAVGANNRETURNSI] : fundData[FIELDS.TCRATERETURNSI];
}

function alignInceptionDate(fundData) {
  return format.oaDate(fundData[FIELDS.TCINCEPTIONDATE]);
}
function alignSalesChargeYearToDateReturn(fundData) {
  return fundData[FIELDS.TCAVGANNUALRTRNYTD_OFFER];
}

function alignSalesChargeOneYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCQAVGANNUALRTRN1YR_OFFER];
  }
  return fundData[FIELDS.TCAVGANNUALRTRN1YR_OFFER];
}

function alignSalesChargeThreeYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCQAVGANNUALRTRN3YR_OFFER];
  }
  return fundData[FIELDS.TCAVGANNUALRTRN3YR_OFFER];
}

function alignSalesChargeFiveYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCQAVGANNUALRTRN5YR_OFFER];
  }
  return fundData[FIELDS.TCAVGANNUALRTRN5YR_OFFER];
}

function alignSalesChargeTenYearReturn(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCQAVGANNUALRTRN10YR_OFFER];
  }
  return fundData[FIELDS.TCAVGANNUALRTRN10YR_OFFER];
}

function alignSalesChargeSinceInception(fundData, performanceQuarterly) {
  if (performanceQuarterly) {
    return fundData[FIELDS.TCQAVGANNUALRTRNSINCEINC_OFFER];
  }
  return fundData[FIELDS.TCAVGANNUALRTRNSINCEINC_OFFER];
}

export const getModels = (types, filterModels, modelMap) => {
  return types.map(productType => {
    return filterModels[modelMap[productType]];
  });
};

export const isShowDebugInfoEnabled = (url = window.location.href) => {
  return (url || '').toLowerCase().includes('..showdebuginfo..=on');
};
